/**
 * Generic form-state persistence service (framework-agnostic).
 *
 * Snapshots a form's state to Session Storage right before the app navigates
 * out (e.g. a Stripe Checkout redirect) and lets it be restored when the user
 * returns to the same route. Session Storage is used deliberately: it is
 * tab-scoped and survives a same-tab `window.location.href` redirect, so the
 * record written before leaving is intact on return.
 *
 * Only ONE pending record is kept at a time — a single external redirect
 * happens at once — under a fixed key. Reads are gated on formId + route +
 * freshness so a stale/foreign record is never restored.
 */

/** Single active slot for the form awaiting an external redirect round-trip. */
export const FORM_PERSIST_KEY = "haire:form-persist:pending";

/** Records older than this are considered stale and are discarded on read. */
export const DEFAULT_TTL_MS = 30 * 60 * 1000; // 30 minutes

const getStorage = () => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    // Accessing sessionStorage can throw (sandboxed iframes, disabled storage).
    return null;
  }
};

/**
 * Persist a form snapshot pending restoration.
 * @param {{ formId: string, route: string, data: any }} params
 * @returns {boolean} true if written
 */
export const saveFormState = ({ formId, route, data }) => {
  const storage = getStorage();
  if (!storage || !formId) return false;
  try {
    const record = {
      formId,
      route,
      isTempPersisted: true,
      timestamp: Date.now(),
      data,
    };
    storage.setItem(FORM_PERSIST_KEY, JSON.stringify(record));
    // Debug: inspect the exact form snapshot persisted to session storage.
    // console.log(`[FormPersistence] saved "/${FORM_PERSIST_KEY}":`, record);
    return true;
  } catch (error) {
    // Quota exceeded or non-serializable data — fail soft, persistence is best-effort.
    console.error("formPersistence.saveFormState failed:", error);
    return false;
  }
};

/**
 * Read the raw pending record (no validation).
 * @returns {{ formId: string, route: string, isTempPersisted: boolean, timestamp: number, data: any } | null}
 */
export const readFormState = () => {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(FORM_PERSIST_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error("formPersistence.readFormState failed:", error);
    return null;
  }
};

/** Remove the pending record. */
export const clearFormState = () => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(FORM_PERSIST_KEY);
  } catch (error) {
    console.error("formPersistence.clearFormState failed:", error);
  }
};

/**
 * Return the pending record only if it is valid for the given form/route and
 * still fresh. Stale records are cleared as a side-effect to prevent leaks.
 * @param {{ formId: string, route: string, ttlMs?: number }} params
 * @returns {object | null} the record, or null when there is nothing to restore
 */
export const getPendingFormState = ({
  formId,
  route,
  ttlMs = DEFAULT_TTL_MS,
}) => {
  const record = readFormState();
  if (!record || !record.isTempPersisted) return null;

  const isStale =
    typeof record.timestamp === "number" &&
    Date.now() - record.timestamp > ttlMs;
  if (isStale) {
    clearFormState();
    return null;
  }

  // Route + form identity must match so a record for another form/page is ignored.
  if (record.formId !== formId || record.route !== route) return null;

  return record;
};
